<template>
  <div class="video-upload-container">
    <b-modal id="video-upload-modal" ref="videoUploadModal" :header-class="isUploading ? 'uploading' : ''" hide-footer @hidden="resetModalValues" @hide="hideEvent" :no-close-on-esc="noCloseOnBackdrop" :no-close-on-backdrop="noCloseOnBackdrop">
      <div v-if="!isUploading" class="upload-conatiner" id="video-drop-area">
        <div>
          <div>Drag and drop your video here.</div>
          <div class="file-input-div">
            <span class="file-button">Or select a video</span>
            <input type="file" accept="video/*" id="video-file-upload" @change="handleFileSelection" />
          </div>
          <div v-if="selectedVideofile" class="file-name">
            {{ selectedVideofile.name }}
          </div>
        </div>
      </div>
      <div v-else class="progress-box">
        <div v-if="conversionInProgress">
          <div class="mb-15 text-center">
            Video conversion is in progress...
          </div>
          <div data-title=".dot-stretching">
            <div class="stage">
              <div class="dot-stretching"></div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="mb-15 text-center">Uploading...</div>
          <div class="progress" style="height: 10px;">
            <div class="progress-bar" role="progressbar" :style="{ width: `${uploadPercentage}%` }" aria-valuenow="uploadPercentage" aria-valuemin="0" aria-valuemax="100"></div>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <div class="footer">
          <b-button :disabled="disableInsertButton" variant="primary" class="float-right" @click="uploadFile">
            Insert
          </b-button>
        </div>
      </template>
    </b-modal>
    <!-- This was included to cover the entire screen & block pointer events, as during uploading process, clicking on the modal hindered iframe to get visible inside quill-editor -->
    <div :class="{ 'pointer-event-none': isUploading }"></div>
  </div>
</template>

<script>
import eventBus from "@/eventBus";
export default {
  name: "video-upload-modal-component",
  props: {
    moduleName: {
      required: false,
      type: String
    }
  },
  data() {
    return {
      noCloseOnBackdrop: true,
      studyId: this.$route.params.studyId,
      isUploading: false,
      selectedVideofile: null,
      uploadPercentage: 0,
      convertedVideoRefreshInterval: null,
      tempVar: 0,
      conversionInProgress: false
    };
  },
  watch: {
    selectedVideofile: {
      handler() {},
      deep: true
    }
  },
  computed: {
    disableInsertButton() {
      return !!(this.isUploading || !this.selectedVideofile);
    }
  },
  beforeDestroy() {
    clearInterval(this.convertedVideoRefreshInterval);
  },
  methods: {
    show() {
      this.$refs.videoUploadModal.show();
      this.$emit("stopAutoSave");
    },
    hide() {
      this.$refs.videoUploadModal.hide();
    },
    resetModalValues() {
      this.isUploading = false;
      this.selectedVideofile = null;
      this.uploadPercentage = 0;
      this.conversionInProgress = false;
    },
    hideEvent(event) {
      if (event.trigger == "header-close") {
        this.$emit("startAutoSave");
      }
    },
    handleDrop(e) {
      this.validateFile(e.dataTransfer.files[0]);
    },
    handleFileSelection(e) {
      this.validateFile(e.target.files[0]);
    },
    validateFile(file) {
      const filename = file.name;
      const fileExtension = filename.substring(filename.lastIndexOf(".") + 1, filename.length);
      const allowedFileTypes = ["video/quicktime", "video/mp4", "video/avi", "video/x-ms-wmv", "video/x-flv"];
      //Validate allowed file types
      if (!allowedFileTypes.includes(file.type) && fileExtension != "flv") {
        this.$toastr.error("The selected file is an invalid video file format.");
      } else if (file.size / 1024 / 1024 > 2048) {
        //Allowed size upto 2GB
        this.$toastr.error("File size exceeds the maximum of 2GB.");
      } else {
        this.selectedVideofile = file;
      }
      document.getElementById("video-file-upload").value = "";
    },
    uploadFile() {
      this.isUploading = true;
      const fd = new FormData();
      const newFileName = this.changeExtensionToLowerCase(this.selectedVideofile);
      fd.append("file", this.selectedVideofile, newFileName);
      const pathParams = {
        studyId: this.studyId
      };
      const queryParams = {
        moduleName: this.moduleName,
        questionId: null
      };
      this.tempVar = 30000;
      this.$api.uploadLocalVideo(pathParams, queryParams, fd).then(
        response => {
          if (this.selectedVideofile.type === "video/mp4") {
            // conversion should not happen for mp4
            this.conversionInProgress = false;
            this.processConvertedUrlResponse(response);
          } else {
            // Reload appointments every 30 seconds
            this.conversionInProgress = true;
            this.convertedVideoRefreshInterval = window.setInterval(() => {
              this.tempVar += 30000;
              this.getConvertedVideoURL(response);
            }, 30000);
          }
        },
        error => {
          this.isUploading = false;
          const errorMsg = error.body ? error.body.message : "Error while uploading video file.";
          this.$toastr.error(errorMsg);
        }
      );
    },
    getConvertedVideoURL(responseData) {
      const videoId = responseData.body.id;
      const pathParams = {
        studyId: this.studyId
      };
      const queryParams = {
        videoId: videoId
      };

      this.$api.getUploadedLocalVideoConvertedURL(pathParams, queryParams).then(
        response => {
          this.processConvertedUrlResponse(response);
        },
        error => {
          clearInterval(this.convertedVideoRefreshInterval);
          this.isUploading = false;
          this.conversionInProgress = false;
          const errorMsg = error.body ? error.body.message : "Error while converting video file.";
          this.$toastr.error(errorMsg);
        }
      );
    },
    processConvertedUrlResponse(response) {
      const data = {
        url: response.body.convertedVideoUrl,
        videoId: response.body.id
      };
      if (response.body.status.toLowerCase() == "failed") {
        clearInterval(this.convertedVideoRefreshInterval);
        this.$toastr.error(response.body.errorText);
        this.conversionInProgress = false;
        this.isUploading = false;
      } else if (response.body.status.toLowerCase() == "success") {
        clearInterval(this.convertedVideoRefreshInterval);
        this.$emit("insertVideo", data);
        this.hide();
      } else if (this.tempVar == 30000) {
        this.$emit("insertVideo", data);
        clearInterval(this.convertedVideoRefreshInterval);
        this.hide();
      }
    },
    changeExtensionToLowerCase(file) {
      const name = file.name;
      const lastIndexOfDot = name.lastIndexOf(".");
      const ext = name.substring(lastIndexOfDot, name.length);
      return name.substring(0, lastIndexOfDot) + ext.toLowerCase();
    }
  },

  mounted() {
    const dropArea = document.getElementById("video-drop-area");
    ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      dropArea.addEventListener(
        eventName,
        function(e) {
          e.preventDefault();
          e.stopPropagation();
        },
        false
      );
    });

    ["dragenter", "dragover"].forEach(eventName => {
      dropArea.addEventListener(
        eventName,
        function() {
          dropArea.classList.add("highlight");
        },
        false
      );
    });
    ["dragleave", "drop"].forEach(eventName => {
      dropArea.addEventListener(
        eventName,
        function() {
          dropArea.classList.remove("highlight");
        },
        false
      );
    });

    dropArea.addEventListener("drop", this.handleDrop, false);

    eventBus.$on("handleUploadProgress", e => {
      this.uploadPercentage = (e.loaded / e.total) * 100;
    });
  }
};
</script>

<style scoped lang="scss" src="@/assets/dot-streaching-loader.scss"></style>
<style lang="scss">
@media (min-width: 992px) {
  #video-upload-modal {
    .modal-dialog {
      min-width: 530px !important;
      vertical-align: middle;
    }
  }
}

#video-upload-modal {
  .modal-content {
    .modal-header {
      border-bottom: 0;
      padding-bottom: 0;

      .close {
        margin-top: -24px;
      }

      &.uploading {
        .close {
          visibility: hidden;
        }
      }
    }

    .modal-body {
      padding-top: 5px;
      padding-left: 30px;
      padding-right: 30px;
      padding-bottom: 22px;
      font-size: 14px;
      color: #4a4a4a;

      .upload-conatiner {
        text-align: center;
        background-color: #ffffff;
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='3' ry='3' stroke='%23C5D1D6FF' stroke-width='4' stroke-dasharray='10%2c 14' stroke-dashoffset='10' stroke-linecap='square'/%3e%3c/svg%3e");
        border-radius: 3px;
        padding: 91px 0 115px;
        max-height: 275px;

        &.highlight {
          background: #f5f8fa;
          background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='3' ry='3' stroke='%234C8CE4FF' stroke-width='4' stroke-dasharray='10%2c 14' stroke-dashoffset='10' stroke-linecap='square'/%3e%3c/svg%3e");
          border-radius: 3px;
        }

        .file-input-div {
          color: #4c8ce4;
          padding: 2px 19px;
          border-radius: 3px;
          border: solid 2px #4c8ce4;
          position: relative;
          display: inline-block;
          width: 158px;
          overflow: hidden;
          cursor: pointer;
          margin-top: 16px;

          #video-file-upload {
            cursor: inherit;
            display: block;
            font-size: 20px;
            filter: alpha(opacity=0);
            width: 158px;
            height: 34px;
            max-height: 34px;
            max-width: 158px;
            opacity: 0;
            position: absolute;
            right: 0;
            text-align: right;
            top: 0;
            left: 0;
          }

          .file-button {
            padding: 5px;
            width: 100%;
            display: block;
            border-radius: 4px;
            text-align: center;
          }
        }

        .file-name {
          color: #c5d1d6;
        }
      }

      .progress-box {
        padding: 103px 50px;
        border: 0;
        background: #f5f8fa;
        //text-align: center;
        max-height: 275px;

        .progress {
          background-color: #c5d1d6;
          border-radius: 4px;

          .progress-bar {
            background-color: #4c8ce4;
            border-radius: 4px;
          }
        }

        .stage {
          display: flex;
          justify-content: center;
        }
      }

      .footer {
        margin-top: 26px;
        overflow: hidden;

        button {
          &.btn-primary {
            background-color: #4c8ce4;
            border-color: #4c8ce4;
            border-radius: 25px;
            font-size: 14px;
            padding: 9px 42px;

            &:hover {
              background-color: #34537d;
              border-color: #34537d;
            }
          }
        }
      }
    }
  }
}
.video-upload-container {
  position: relative;
  .pointer-event-none {
    position: fixed;
    background-color: transparent;
    left: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999999999 !important;
    cursor: default;
  }
}
</style>
